import { useEffect } from "react";
import logo from '../../Img/logo.png';

export default function Home() {
  useEffect(() => {
    // Tema oscuro y fuente suave
    const root = document.documentElement;
    root.style.setProperty("--bg", "#0b0f14");
    root.style.setProperty("--surface", "#0f141a");
    root.style.setProperty("--muted", "#8896a7");
    root.style.setProperty("--text", "#e6edf3");
    root.style.setProperty("--primary", "#46d07f");
    root.style.setProperty("--accent", "#8ab4ff");
    root.style.setProperty("--border", "rgba(255,255,255,0.08)");
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div style={styles.page}>
      <Nav onNav={scrollTo} />
      <Hero onPrimary={() => scrollTo("contacto")} onSecondary={() => scrollTo("dashboard")} />
      <Trust />
      <Servicios />
      <HowItWorks />
      <Dashboard />
      <Features />
      <FAQ />
      <Contacto />
      <Footer />
    </div>
  );
}

/* ================= NAVBAR ================= */
function Nav({ onNav }) {
  return (
    <header style={styles.nav}>
      <div style={styles.navInner}>
        <div style={styles.brand} onClick={() => onNav("hero")}>
          <img src={logo} alt="" style={{ width: "5%" }} />
          <span style={{ fontWeight: 700, letterSpacing: 0.4 }}>QuantumNodo</span>
        </div>

        <nav style={styles.navLinks}>
          <button style={styles.navBtn} onClick={() => onNav("servicios")}>Servicios</button>
          <button style={styles.navBtn} onClick={() => onNav("how")}>Cómo funciona</button>
          <button style={styles.navBtn} onClick={() => onNav("dashboard")}>Dashboard</button>
          <button style={styles.navBtn} onClick={() => onNav("faq")}>FAQ</button>
        </nav>

        <div style={{ display: "flex" }}>
          <a
            href="https://wa.me/5491155890884?text=Hola%20QuantumNodo%2C%20quiero%20automatizar%20mis%20tarea"
            target="_blank"
            rel="noreferrer"
            style={styles.whatsapp}
            aria-label="Hablar por WhatsApp"
          >
            <span className="hide-sm">Empezar</span>
          </a>
          <a
            href="/login"
            rel="noreferrer"
            style={styles.login}
            aria-label="login"
          >
            <span className="hide-sm">Login</span>
          </a>
        </div>
      </div>
    </header>
  );
}

/* ================= HERO ================= */
function Hero({ onPrimary, onSecondary }) {
  return (
    <section id="hero" style={styles.sectionHero}>
      <div style={styles.heroGrid}>
        <div style={styles.heroText}>
          <h1 style={styles.h1}>
            Automatizá tu trabajo con <span style={{ color: "var(--primary)" }}>QuantumNodo</span>
          </h1>
          <p style={styles.lead}>
            Somos <strong>QuantumNodo</strong>, un estudio freelance que crea <em>flujos a medida</em>:
            facturación automática, bots de WhatsApp y email, reportes programados, integraciones y más.
            100% seguro, eficiente y visible en un <b>dashboard</b> en la misma página.
          </p>

          <div style={styles.ctaRow}>
            <button style={styles.primaryBtn} onClick={onPrimary}>Quiero empezar</button>
            <button style={styles.ghostBtn} onClick={onSecondary}>Ver demo del dashboard</button>
          </div>

          <ul style={styles.badges}>
            <li>✔ Ahorro de horas reales</li>
            <li>✔ Soporte por WhatsApp</li>
            <li>✔ Integraciones ilimitadas</li>
          </ul>
        </div>

        {/* Ilustración minimal: oculta en móviles */}
        <div style={styles.heroArt} className="hide-sm">
          <AutomationArt />
        </div>
      </div>
    </section>
  );
}

/* ================= TRUST (mini) ================= */
function Trust() {
  return (
    <section style={styles.trust}>
      <div style={styles.trustRow}>
        <Chip>Factura automática (AFIP)</Chip>
        <Chip>Bots de WhatsApp & Email</Chip>
        <Chip>Scraping & ETL</Chip>
        <Chip>Reportes & Alerts</Chip>
        <Chip>Webhooks & APIs</Chip>
      </div>
    </section>
  );
}

/* ================= SERVICIOS ================= */
function Servicios() {
  const items = [
    { title: "Facturación automática", desc: "Leé mails/pedidos y emití facturas sin tocar nada. Validación AFIP y comprobantes al cliente." },
    { title: "Bots de WhatsApp / Email", desc: "Respuestas 24/7, seguimientos, recordatorios, adjuntos y flujos condicionales." },
    { title: "Integraciones n8n", desc: "Conectamos CRMs, Google, ERPs, Sheets, APIs propias y más. Sin fricción." },
    { title: "Scraping & ETL", desc: "Extraé datos, limpiá y cargá a tu base o dashboard para decisiones rápidas." },
    { title: "Reportes programados", desc: "KPIs diarios/semanales al mail/WhatsApp. PDF/CSV, gráficos y adjuntos." },
    { title: "Webhooks & APIs", desc: "Endpoints seguros para disparar procesos desde tu web, app o checkout." },
  ];
  return (
    <section id="servicios" style={styles.section}>
      <h2 style={styles.h2}>Servicios</h2>
      <p style={styles.pCenter}>
        Todo lo que podés construir con <b>n8n</b>, pero <em>a medida</em> para tu negocio.
      </p>
      <div style={styles.grid3}>
        {items.map((it) => (
          <div key={it.title} style={styles.card}>
            <h3 style={styles.h3}>{it.title}</h3>
            <p style={styles.cardP}>{it.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================= HOW IT WORKS ================= */
function HowItWorks() {
  const steps = [
    { n: 1, t: "Kickoff", d: "Conversamos 30' sobre tus tareas repetitivas y objetivos." },
    { n: 2, t: "Diseño del flujo", d: "Prototipo del flujo en n8n con tus herramientas." },
    { n: 3, t: "Implementación segura", d: "Credenciales cifradas, pruebas y logs." },
    { n: 4, t: "Dashboard", d: "Panel en tu landing para monitorear y disparar tareas." },
  ];
  return (
    <section id="how" style={styles.section}>
      <h2 style={styles.h2}>¿Cómo trabajamos?</h2>
      <div style={styles.steps}>
        {steps.map((s) => (
          <div key={s.n} style={styles.step}>
            <div style={styles.stepBadge}>{s.n}</div>
            <div>
              <h4 style={{ margin: 0 }}>{s.t}</h4>
              <p style={{ margin: "6px 0 0", color: "var(--muted)" }}>{s.d}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================= DASHBOARD ================= */
function Dashboard() {
  return (
    <section id="dashboard" style={styles.section}>
      <h2 style={styles.h2}>Dashboard en tu página</h2>
      <p style={styles.pCenter}>
        Ejecutá flujos, mirá métricas y revisá logs sin salir de tu sitio.
      </p>
      <div style={styles.dashboardWrap}>
        <div style={styles.dashboardHeader}>
          <div style={styles.dot} />
          <div style={styles.dot} />
          <div style={styles.dot} />
          <span style={{ color: "var(--muted)" }}>demo.Quantumnodo</span>
        </div>

        <div style={styles.dashboardBody}>
          <div style={styles.kpis}>
            <KPI label="Flujos activos" value="12" />
            <KPI label="Ejecuciones hoy" value="238" />
            <KPI label="Éxito" value="99.2%" />
            <KPI label="Ahorro (hs/mes)" value="46" />
          </div>

          <div style={styles.chartBox}>
            <MiniChart />
          </div>

          <div style={styles.tableBox}>
            <TableLog />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================= FEATURES (seguridad/eficiencia/soporte) ================= */
function Features() {
  const feats = [
    { title: "Seguridad primero", desc: "Credenciales cifradas, scoping por flujo y control de acceso." },
    { title: "Eficiencia real", desc: "Menos clicks, más resultados. Procesos 24/7 sin errores humanos." },
    { title: "Soporte por WhatsApp", desc: "Canal directo para priorizar incidentes y cambios rápidos." },
  ];
  return (
    <section style={styles.section}>
      <div style={styles.grid3}>
        {feats.map((f) => (
          <div key={f.title} style={styles.card}>
            <h3 style={styles.h3}>{f.title}</h3>
            <p style={styles.cardP}>{f.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================= PRECIOS ================= */
/*function Precios() {
  const plans = [
    {
      name: "Starter",
      price: "US$ 149",
      feat: ["1 flujo a medida", "Dashboard básico", "Soporte por WhatsApp", "Entrega en 7 días"],
    },
    {
      name: "Pro",
      price: "US$ 349",
      feat: ["3 flujos a medida", "Dashboard completo", "Alertas & reportes", "Entrega en 10 días"],
      highlight: true,
    },
    {
      name: "Empresa",
      price: "A convenir",
      feat: ["Flujos ilimitados", "SLAs & monitoreo", "Integraciones complejas", "Onboarding del equipo"],
    },
  ];
  return (
    <section id="precios" style={styles.section}>
      <h2 style={styles.h2}>Planes</h2>
      <div style={styles.pricing}>
        {plans.map((p) => (
          <div key={p.name} style={{ ...styles.priceCard, ...(p.highlight ? styles.priceCardHL : {}) }}>
            <div style={styles.priceHead}>
              <h3 style={{ margin: 0 }}>{p.name}</h3>
              <div style={styles.price}>{p.price}</div>
            </div>
            <ul style={styles.priceList}>
              {p.feat.map((f) => <li key={f}>• {f}</li>)}
            </ul>
            <a
              href="https://wa.me/5491155890884?text=Quiero%20este%20plan%20de%20QuantumNodo"
              target="_blank"
              rel="noreferrer"
              style={styles.primaryBtnBlock}
            >
              Elegir plan
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================= FAQ ================= */
function FAQ() {
  const faqs = [
    {
      q: "¿Qué es QuantumNodo?",
      a: "Es una plataforma de automatización de flujos que conecta apps y APIs para ejecutar procesos sin intervención manual.",
    },
    {
      q: "¿Necesito servidores propios?",
      a: "Podemos desplegar en tu infraestructura o usar un hosting gestionado. Te asesoramos según tu caso.",
    },
    {
      q: "¿Se puede integrar con AFIP, Gmail, WhatsApp, Sheets…?",
      a: "Sí. Integramos servicios oficiales o vía API/webhooks. Si no existe, desarrollamos el conector.",
    },
    {
      q: "¿Cómo veo lo que corre?",
      a: "Te dejamos un dashboard embebido con métricas, ejecuciones y controles para disparar flujos.",
    },
  ];
  return (
    <section id="faq" style={styles.section}>
      <h2 style={styles.h2}>Preguntas frecuentes</h2>
      <div style={styles.faqWrap}>
        {faqs.map(({ q, a }) => (
          <details key={q} style={styles.faqItem}>
            <summary style={styles.faqQ}>{q}</summary>
            <p style={styles.faqA}>{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ================= CONTACTO ================= */
function Contacto() {
  return (
    <section id="contacto" style={styles.sectionCTA}>
      <div style={styles.ctaCard}>
        <h2 style={{ margin: 0 }}>¿Automatizamos tu negocio?</h2>
        <p style={{ marginTop: 8, color: "var(--muted)" }}>
          Contanos qué tareas querés simplificar y armamos tu flujo a medida.
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <a
            href="https://wa.me/5491155890884?text=Hola%20QuantumNodo%2C%20necesito%20automatizar..."
            target="_blank"
            rel="noreferrer"
            style={styles.primaryBtn}
          >
            <WhatsIcon /> Hablar por WhatsApp
          </a>
          <a href="mailto:hola@Quantumnodo.com" style={styles.ghostBtn}>
            Escribir por Email
          </a>
        </div>
      </div>
    </section>
  );
}

/* ================= FOOTER ================= */
function Footer() {
  return (
    <footer style={styles.footer}>
      <div style={styles.footerInner}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Logo size={16} />
          <strong>QuantumNodo</strong>
        </div>
        <div style={{ color: "var(--muted)" }}>
          © {new Date().getFullYear()} QuantumNodo — Automatización con n8n
        </div>
      </div>

      {/* Helpers para responsive */}
      <style>{`
        .hide-sm { display: block; }
        @media (max-width: 900px) {
          .hide-sm { display: none !important; }
        }
      `}</style>
    </footer>
  );
}

/* ================== SMALL UI PIECES ================== */
function Logo({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <circle cx="12" cy="12" r="10" fill="none" stroke="var(--primary)" strokeWidth="2" />
      <path d="M5 12h14M12 5v14" stroke="var(--accent)" strokeWidth="2" />
    </svg>
  );
}

function WhatsIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" style={{ marginRight: 6 }}>
      <path fill="currentColor" d="M20.5 3.5A10.5 10.5 0 0 0 3.7 19.7L3 22l2.4-.6A10.5 10.5 0 1 0 20.5 3.5Zm-8.4 3.1c.3 0 .7 0 1 .1c.3.1.7.3.8.5c.2.2.2.5.1.8l-.3 1c-.1.3-.2.5-.5.6c-.2.1-.4.1-.7 0c-.2 0-.4-.2-.6-.3l-.3-.2c-.2-.2-.4-.2-.6 0c-.2.3-.6.6-.7.9c-.2.3-.1.6.1.9c.4.6.9 1.1 1.5 1.6c.7.5 1.4.9 2.2 1.1c.3.1.6.1.9 0c.3-.1.6-.3.8-.6c.2-.3.3-.7.6-.9c.2-.1.5-.2.8-.1l1 .3c.3.1.6.3.7.6c.1.3.1.6 0 .9c-.2.6-.6 1.1-1.1 1.5c-.5.4-1.1.6-1.7.7h-.7c-1.2-.1-2.4-.4-3.4-1c-1.1-.5-2-1.3-2.8-2.2c-.7-.8-1.3-1.8-1.6-2.9c-.3-1.1-.3-2.2 0-3.3c.2-.7.5-1.3 1-1.8c.4-.5 1-.8 1.6-1c.4-.1.8-.1 1.1-.1Z" />
    </svg>
  );
}

function Chip({ children }) {
  return (
    <span style={styles.chip}>{children}</span>
  );
}

function KPI({ label, value }) {
  return (
    <div style={styles.kpi}>
      <div style={{ fontSize: 28, fontWeight: 800 }}>{value}</div>
      <div style={{ fontSize: 12, color: "var(--muted)" }}>{label}</div>
    </div>
  );
}

function MiniChart() {
  // SVG sparkline minimal
  return (
    <svg viewBox="0 0 300 100" style={{ width: "100%", height: 120 }}>
      <polyline
        fill="none"
        stroke="var(--accent)"
        strokeWidth="3"
        points="0,70 30,65 60,72 90,50 120,55 150,35 180,40 210,30 240,36 270,20 300,24"
      />
      <line x1="0" y1="80" x2="300" y2="80" stroke="var(--border)" />
    </svg>
  );
}

function TableLog() {
  const rows = [
    { time: "10:12", event: "Factura emitida", status: "OK", id: "#FA-1032" },
    { time: "10:05", event: "Bot WhatsApp respondió", status: "OK", id: "#MSG-284" },
    { time: "09:58", event: "ETL completado", status: "OK", id: "#ETL-019" },
    { time: "09:43", event: "Webhook disparado", status: "OK", id: "#WBK-771" },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th>Hora</th>
            <th>Evento</th>
            <th>Estado</th>
            <th>Ref</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.time}</td>
              <td>{r.event}</td>
              <td><span style={styles.pillOk}>OK</span></td>
              <td>{r.id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AutomationArt() {
  // composicion geométrica simple
  return (
    <svg viewBox="0 0 420 360" style={{ width: "100%", height: "auto" }}>
      <rect x="0" y="0" width="420" height="360" rx="20" fill="var(--surface)" stroke="var(--border)" />
      <g transform="translate(40,40)">
        <rect width="160" height="90" rx="12" fill="none" stroke="var(--accent)" />
        <rect x="180" width="160" height="90" rx="12" fill="none" stroke="var(--primary)" />
        <rect y="110" width="160" height="90" rx="12" fill="none" stroke="var(--primary)" />
        <rect x="180" y="110" width="160" height="90" rx="12" fill="none" stroke="var(--accent)" />
        <path d="M80,90 L80,110 M260,90 L260,110" stroke="var(--border)" />
        <circle cx="80" cy="200" r="6" fill="var(--primary)" />
        <circle cx="260" cy="200" r="6" fill="var(--accent)" />
        <path d="M86,200 C120,180 220,180 254,200" stroke="var(--border)" fill="none" />
        <text x="0" y="230" fill="var(--muted)" fontSize="12">Flujos conectados</text>
      </g>
    </svg>
  );
}

/* ================== STYLES ================== */
const styles = {
  page: { background: "var(--bg)", color: "var(--text)", fontFamily: "Inter, system-ui, Arial, sans-serif" },

  nav: { position: "sticky", top: 0, zIndex: 50, backdropFilter: "saturate(120%) blur(8px)" },
  navInner: {
    maxWidth: 1180, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "rgba(11,15,20,0.7)"
  },
  brand: { display: "flex", alignItems: "center", gap: 10, cursor: "pointer" },
  navLinks: { display: "flex", gap: 14, alignItems: "center" },
  navBtn: {
    background: "transparent", border: "1px solid transparent", color: "var(--muted)", padding: "8px 10px",
    borderRadius: 10, cursor: "pointer"
  },
  whatsapp: {
    display: "flex", alignItems: "center", gap: 6, background: "rgba(70,208,127,0.15)", color: "var(--primary)",
    padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(70,208,127,0.35)", textDecoration: "none", marginRight: "1rem", fontWeight: 600
  },
  login: {
    display: "flex", alignItems: "center", gap: 6, background: "rgba(255, 255, 255, 0.04)", color: "white",
    padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(255, 255, 255, 0.35)", textDecoration: "none", fontWeight: 600
  },

  section: { maxWidth: 1180, margin: "0 auto", padding: "80px 20px" },
  sectionHero: { maxWidth: 1180, margin: "0 auto", padding: "72px 20px" },
  h1: { fontSize: 44, lineHeight: 1.1, margin: "10px 0 16px" },
  h2: { fontSize: 32, margin: "0 0 18px" },
  h3: { fontSize: 18, margin: "0 0 8px" },
  pCenter: { color: "var(--muted)", textAlign: "center", marginBottom: 24 },

  heroGrid: { display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 28, alignItems: "center" },
  heroText: { padding: 10 },
  lead: { color: "var(--muted)", fontSize: 18, lineHeight: 1.6 },
  heroArt: { border: "1px solid var(--border)", borderRadius: 18, overflow: "hidden", background: "var(--surface)" },

  ctaRow: { display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" },
  primaryBtn: {
    background: "var(--primary)", color: "#08130c", padding: "12px 16px", borderRadius: 12, border: "none",
    fontWeight: 700, cursor: "pointer"
  },
  primaryBtnBlock: {
    display: "block", textAlign: "center", background: "var(--primary)", color: "#08130c",
    padding: "12px 16px", borderRadius: 12, textDecoration: "none", fontWeight: 700
  },
  ghostBtn: {
    background: "transparent", color: "var(--text)", padding: "12px 16px", borderRadius: 12,
    border: "1px solid var(--border)", fontWeight: 600, cursor: "pointer", textDecoration: "none"
  },

  badges: { display: "flex", gap: 14, marginTop: 16, padding: 0, listStyle: "none", color: "var(--muted)", flexWrap: "wrap" },

  trust: { padding: "10px 20px 0" },
  trustRow: { maxWidth: 1180, margin: "0 auto", display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" },
  chip: {
    background: "var(--surface)", border: "1px solid var(--border)", padding: "8px 12px", borderRadius: 999,
    color: "var(--muted)", fontSize: 13
  },

  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 16,
  },
  card: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.00))",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 18
  },
  cardP: { color: "var(--muted)", margin: 0 },

  steps: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 },
  step: { display: "flex", gap: 12, alignItems: "flex-start", background: "var(--surface)", border: "1px solid var(--border)", padding: 16, borderRadius: 14 },
  stepBadge: { width: 30, height: 30, borderRadius: 8, display: "grid", placeItems: "center", background: "rgba(138,180,255,0.12)", color: "var(--accent)", fontWeight: 800 },

  dashboardWrap: { border: "1px solid var(--border)", borderRadius: 18, overflow: "hidden", background: "var(--surface)" },
  dashboardHeader: { display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid var(--border)" },
  dot: { width: 8, height: 8, borderRadius: 999, background: "var(--border)" },
  dashboardBody: { padding: 16, display: "grid", gap: 16 },
  kpis: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 },
  kpi: { background: "rgba(255,255,255,0.02)", padding: 14, borderRadius: 12, border: "1px solid var(--border)" },
  chartBox: { background: "rgba(255,255,255,0.02)", padding: 10, borderRadius: 12, border: "1px solid var(--border)" },
  tableBox: { background: "rgba(255,255,255,0.02)", padding: 10, borderRadius: 12, border: "1px solid var(--border)" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  pillOk: {
    display: "inline-block", padding: "2px 8px", borderRadius: 999,
    background: "rgba(70,208,127,0.15)", color: "var(--primary)", border: "1px solid rgba(70,208,127,0.35)", fontSize: 12
  },
  tableThTd: {},
  footer: { padding: "30px 20px", borderTop: "1px solid var(--border)", marginTop: 40 },
  footerInner: { maxWidth: 1180, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" },

  pricing: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, alignItems: "stretch" },
  priceCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 18, display: "grid", gridTemplateRows: "auto 1fr auto", gap: 14 },
  priceCardHL: { outline: "2px solid rgba(70,208,127,0.5)" },
  priceHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between" },
  price: { fontWeight: 800, fontSize: 22 },
  priceList: { margin: 0, paddingLeft: 18, color: "var(--muted)" },

  faqWrap: { maxWidth: 900, margin: "0 auto", display: "grid", gap: 10 },
  faqItem: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "12px 14px" },
  faqQ: { cursor: "pointer", fontWeight: 700 },
  faqA: { margin: "8px 0 0", color: "var(--muted)" },

  sectionCTA: { padding: "70px 20px" },
  ctaCard: {
    maxWidth: 980, margin: "0 auto", background: "linear-gradient(180deg, rgba(70,208,127,0.08), rgba(255,255,255,0.02))",
    border: "1px solid var(--border)", borderRadius: 18, padding: 24, textAlign: "center"
  },

  // Responsive
  "@media": {},
};

/* ======= Responsive tweaks via injected CSS ======= */
const responsiveCSS = `
  @media (max-width: 1100px) {
    h1 { font-size: 38px !important; }
  }
  @media (max-width: 900px) {
    .heroGrid { grid-template-columns: 1fr !important; }
    .grid3 { grid-template-columns: 1fr !important; }
    .steps { grid-template-columns: 1fr 1fr !important; }
    .kpis { grid-template-columns: 1fr 1fr !important; }
    .pricing { grid-template-columns: 1fr !important; }
  }
`;
if (typeof document !== "undefined") {
  const already = document.getElementById("qn-responsive");
  if (!already) {
    const style = document.createElement("style");
    style.id = "qn-responsive";
    style.innerHTML = responsiveCSS
      .replace(".heroGrid", "")
      .replace(".grid3", "")
      .replace(".steps", "")
      .replace(".kpis", "")
      .replace(".pricing", "");
    document.head.appendChild(style);
  }
}
